import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap, filter, take, finalize, map } from 'rxjs/operators';
import { User, ProfileUpdateRequest, ProfileUpdateResponse, GenericResponse } from '../../../core/interface';
import { Urls } from '../../../shared/url';
import { jwtDecode } from 'jwt-decode';
import { TokenService } from '../../core/service/token.service';
import { Router } from '@angular/router';

interface DecodedToken {
  exp: number;
  is_superadmin?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  // Private BehaviorSubject for internal state management
  private _authUser = new BehaviorSubject<User | null>(null);
  private _isSuperAdmin = new BehaviorSubject<boolean>(false);
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  // Public observable for components to subscribe to
  public authUser$ = this._authUser.asObservable();
  public isSuperAdmin$ = this._isSuperAdmin.asObservable();

  constructor(
    private readonly _http: HttpClient,
    private tokenService: TokenService,
    private router: Router
  ) {}

  // Initialize authentication state based on stored tokens
  initializeAuthState(): void {
    const accessToken = this.tokenService.getAccessToken();

    if (accessToken && !this.tokenService.isAccessTokenExpired()) {
      // Case 1: Valid access token exists. Fetch user profile.
      this.getUserProfile().subscribe({
        next: (response) => {
          this._authUser.next(response.data);
          this.decodeToken(accessToken);
        },
        error: (err) => {
          // Profile fetch failed, clear tokens and state
          this.clearTokens();
        }
      });
    } else {
      // Case 2: Access token is missing or expired.
      const refreshToken = this.tokenService.getRefreshToken();
      if (refreshToken) {
        // Case 2a: We have a refresh token. Try to get a new access token.
        this.refreshToken().subscribe({
          error: () => {
            // Refresh token is invalid or expired. Log the user out.
            this.clearTokens();
          }
        });
      } else {
        // Case 2b: No access token, no refresh token. User is logged out.
        this.clearTokens();
      }
    }
  }

  // Get current user value synchronously
  get currentUser(): User | null {
    return this._authUser.value;
  }

  // Check if user is authenticated
  get isAuthenticated(): boolean {
    return this._authUser.value !== null;
  }

  // Get user profile from the server
  getUserProfile(): Observable<GenericResponse<User>> {
    return this._http.get<GenericResponse<User>>(`${Urls.apiBaseUrl}/auth/me`)
      .pipe(
        catchError(this.handleError)
      );
  }

  googleAuth(googleIdToken: string): Observable<GenericResponse<any>> {
    const headers = new HttpHeaders({
      'GOOGLE-ID-TOKEN': googleIdToken
    });
    
    return this._http.post<GenericResponse<any>>(`${Urls.apiBaseUrl}/auth/gauth`, {}, { headers })
      .pipe(
        tap((response: GenericResponse<any>) => {
          if (response.success && response.data) {
            // Store tokens if authentication is successful
            this.tokenService.setTokens(response.data.access_token, response.data.refresh_token);
            
            // Get user profile after successful authentication
            this.getUserProfile().subscribe({
              next: (profileResponse) => {
                this._authUser.next(profileResponse.data);
                const token = this.tokenService.getAccessToken();
                if (token) {
                  this.decodeToken(token);
                }
              },
              error: (error) => {
                console.error('Failed to get user profile after login:', error);
              }
            });
          }
        }),
        catchError(this.handleError)
      );
  }

  refreshToken(): Observable<any> {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1)
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    const refreshToken = this.tokenService.getRefreshToken();
    if (!refreshToken) {
      this.isRefreshing = false;
      this.clearTokens();
      return throwError(() => new Error('No refresh token available.'));
    }

    return this.requestToken(refreshToken).pipe(
      tap((response: GenericResponse<any>) => {
        this.tokenService.setTokens(response.data.access_token, response.data.refresh_token);
        this.decodeToken(response.data.access_token);
        this.refreshTokenSubject.next(response.data.access_token);

        this.getUserProfile().subscribe(profileResponse => {
            this._authUser.next(profileResponse.data);
        });
      }),
      catchError((error) => {
        this.clearTokens();
        return throwError(() => error);
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }

  requestToken(requestToken: string): Observable<GenericResponse<any>> {
    return this._http.post<GenericResponse<any>>(`${Urls.apiBaseUrl}/auth/refresh-token`, {
      refresh_token: requestToken,
    });
  }

  logout(): Observable<boolean> {
    this.clearTokens();
    this._authUser.next(null);
    this._isSuperAdmin.next(false);
    return of(true);
  }

  updateProfile(profileUpdateRequest: ProfileUpdateRequest): Observable<GenericResponse<ProfileUpdateResponse>> {
    return this._http.put<GenericResponse<ProfileUpdateResponse>>(`${Urls.apiBaseUrl}/auth/update_profile`, profileUpdateRequest).pipe(
      catchError(this.handleError)
    );
  }

  getAccessToken(): string | null {
    return this.tokenService.getAccessToken();
  }

  getRefreshToken(): string | null {
    return this.tokenService.getRefreshToken();
  }

  clearTokens(): void {
    this.tokenService.clearTokens();
    this._authUser.next(null);
    this._isSuperAdmin.next(false);
  }

  isAccessTokenExpired(): boolean {
    return this.tokenService.isAccessTokenExpired();
  }

  /**
   * Fetch the latest user profile and update the global user state
   */
  refreshUserProfile(): Observable<User | null> {
    return this.getUserProfile().pipe(
      tap((response) => {
        if (response.success && response.data) {
          this._authUser.next(response.data);
        }
      }),
      switchMap((response) => of(response.success && response.data ? response.data : null)),
      catchError(() => {
        this._authUser.next(null);
        return of(null);
      })
    );
  }

  private decodeToken(token: string): void {
    try {
      const decoded: DecodedToken = jwtDecode(token);
      this._isSuperAdmin.next(decoded.is_superadmin || false);
    } catch (e) {
      this._isSuperAdmin.next(false);
    }
  }

  private handleError(error: HttpErrorResponse) {
    // Handle network errors
    if (error.error instanceof Error) {
      const errMessage = error.error.message;
      return throwError(() => ({ message: errMessage }));
    }
    
    // Handle HTTP error responses
    if (error.error && typeof error.error === 'object') {
      // Return the error object as-is so the component can access error.error.message
      return throwError(() => error);
    }
    
    // Handle other errors
    const errorObj = {
      message: error.message || 'Something went wrong',
      status: error.status
    };
    return throwError(() => ({ error: errorObj }));
  }
}
